// src/Dashboard.js
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './App.css';
import BankConnectCard from './BankConnectCard';

const API = 'https://snap2excel-backend.onrender.com';

export default function Dashboard({ token, embedded = false }) {
  const [moneyPlan, setMoneyPlan] = useState(null);   // /api/money/plan (kept for later)
  const [receipts, setReceipts] = useState([]);       // /api/receipts
  const [tip, setTip] = useState(null);               // /api/tip
  const [insights, setInsights] = useState([]);       // /api/insights/spending

  const [goals, setGoals] = useState([]);             // local goals
  const [streak, setStreak] = useState(0);            // local streak
  const [notes, setNotes] = useState('');             // local notes
  const [bankConnected, setBankConnected] = useState(false);

  const navigate = useNavigate();

  const authAxios = axios.create({
    baseURL: API,
    headers: { Authorization: `Bearer ${token}` },
  });

  // ---------- Per-user localStorage prefix ----------
  const userKeyPrefix = token ? `s2e_${token.slice(0, 8)}_` : 's2e_guest_';

  /* ========== Local goals / streak / notes from localStorage ========== */
  useEffect(() => {
    const goalsKey = `${userKeyPrefix}goals`;
    const notesKey = `${userKeyPrefix}notes`;
    const lastOpenedKey = `${userKeyPrefix}last_opened`;
    const streakKey = `${userKeyPrefix}streak`;

    const storedGoals = localStorage.getItem(goalsKey);
    if (storedGoals) {
      try {
        setGoals(JSON.parse(storedGoals));
      } catch {
        setGoals([]);
      }
    } else {
      setGoals([]);
    }

    const storedNotes = localStorage.getItem(notesKey) || '';
    setNotes(storedNotes);

    const today = new Date().toDateString();
    const lastDay = localStorage.getItem(lastOpenedKey);
    let newStreak = Number(localStorage.getItem(streakKey) || 0);

    if (!lastDay || lastDay !== today) {
      newStreak += 1;
      localStorage.setItem(lastOpenedKey, today);
      localStorage.setItem(streakKey, String(newStreak));
    }
    setStreak(newStreak);
  }, [userKeyPrefix]);

  const saveGoals = (nextGoals) => {
    setGoals(nextGoals);
    localStorage.setItem(`${userKeyPrefix}goals`, JSON.stringify(nextGoals));
  };

  const addGoal = () => {
    const name = window.prompt('Goal name? (e.g. Holiday, New phone)');
    if (!name) return;
    const targetStr = window.prompt('Target amount (£)?');
    const target = Number(targetStr);
    if (!target || target <= 0) return;

    const next = [...goals, { id: Date.now(), name, target, saved: 0 }];
    saveGoals(next);
  };

  const updateGoalSaved = (goal) => {
    const extraStr = window.prompt(
      `Add amount towards "${goal.name}" (current saved: £${goal.saved.toFixed(
        2
      )})`
    );
    const extra = Number(extraStr);
    if (!extra || Number.isNaN(extra)) return;

    const next = goals.map((g) =>
      g.id === goal.id ? { ...g, saved: g.saved + extra } : g
    );
    saveGoals(next);
  };

  const deleteGoal = (goal) => {
    if (!window.confirm(`Delete goal "${goal.name}"?`)) return;
    const next = goals.filter((g) => g.id !== goal.id);
    saveGoals(next);
  };

  /* ========== Load data from backend (plan + receipts + optional extras) ========== */
  useEffect(() => {
    const fetchAll = async () => {
      if (!token) return;

      try {
        const [planRes, recRes] = await Promise.all([
          authAxios.get('/api/money/plan'),
          authAxios.get('/api/receipts'),
        ]);

        setMoneyPlan(
          planRes.data && planRes.data.hasData ? planRes.data : null
        );

        // Accept both plain array and { receipts: [...] }
        let recArray = [];
        const recData = recRes.data;
        if (Array.isArray(recData)) {
          recArray = recData;
        } else if (recData && Array.isArray(recData.receipts)) {
          recArray = recData.receipts;
        }
        setReceipts(recArray);

        // Optional extras – ignore errors (403 etc.)
        try {
          const tipRes = await authAxios.get('/api/tip');
          setTip(tipRes.data?.tip || null);
        } catch {
          setTip(null);
        }

        try {
          const insightRes = await authAxios.get('/api/insights/spending');
          setInsights(insightRes.data?.insights || []);
        } catch {
          setInsights([]);
        }
      } catch (err) {
        console.error('Dashboard load error', err);
      }
    };

    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  /* ========== Bank connection callback flag ========== */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('bank') === 'connected') {
      setBankConnected(true);
    }
  }, []);

  /* ========== Helper: safe date parser for DD/MM/YYYY & YYYY-MM-DD ========== */
  const parseDate = (dateStr) => {
    if (!dateStr) return null;
    const s = String(dateStr).trim();
    // Try ISO first
    const iso = Date.parse(s);
    if (!Number.isNaN(iso)) return new Date(iso);

    // Try patterns like 05/12/2025 or 5-12-25
    const m = s.match(/(\d{1,4})[\/\-\.](\d{1,2})[\/\-\.](\d{1,4})/);
    if (!m) return null;

    let a = parseInt(m[1], 10);
    let b = parseInt(m[2], 10);
    let c = parseInt(m[3], 10);

    let year, month, day;
    if (a > 1900) {
      // YYYY-MM-DD
      year = a;
      month = b;
      day = c;
    } else if (c > 1900) {
      // DD/MM/YYYY
      year = c;
      month = b;
      day = a;
    } else {
      // Fallback: assume last part is year
      year = c < 100 ? 2000 + c : c;
      month = b;
      day = a;
    }

    if (!month || !day) return null;
    const d = new Date(year, month - 1, day);
    if (Number.isNaN(d.getTime())) return null;
    return d;
  };

  /* ========== Derived metrics from RECEIPTS only ========== */

  const now = new Date();

  // Last 7 days spend
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 6);
  const weekSpend = receipts
    .filter((r) => {
      const d = parseDate(r.date);
      if (!d) return false;
      return d >= sevenDaysAgo && d <= now;
    })
    .reduce((sum, r) => sum + (Number(r.total) || 0), 0);

  // Category breakdown
  const categoryTotals = receipts.reduce((acc, r) => {
    const cat = r.category || 'Other';
    const amt = Number(r.total) || 0;
    acc[cat] = (acc[cat] || 0) + amt;
    return acc;
  }, {});

  const categoryList = Object.entries(categoryTotals)
    .map(([name, total]) => ({ name, total }))
    .sort((a, b) => b.total - a.total)
    .slice(0, 5);

  // Weekly trend bars (last 7 days)
  const trendByDay = (() => {
    const map = {};
    const labels = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date();
      d.setDate(now.getDate() - i);
      const key = d.toISOString().slice(0, 10);
      const label = d.toLocaleDateString('en-GB', { weekday: 'short' });
      labels.push({ key, label });
      map[key] = 0;
    }

    receipts.forEach((r) => {
      const d = parseDate(r.date);
      if (!d) return;
      const k = d.toISOString().slice(0, 10);
      if (map[k] != null) {
        map[k] += Number(r.total) || 0;
      }
    });

    const max = Math.max(...Object.values(map), 1);
    return labels.map(({ key, label }) => ({
      label,
      value: map[key],
      height: (map[key] / max) * 100,
    }));
  })();

  const trendMax =
    trendByDay.length > 0
      ? trendByDay.reduce(
          (best, d) => (d.value > best.value ? d : best),
          trendByDay[0]
        )
      : { label: '-', value: 0 };

  /* ========== Main content (re-used for embedded & full) ========== */

  const content = (
    <>
      {/* ===== HABITS & GOALS ===== */}
      <section className="dash-section">
        <div className="dash-section-header">
          <h2 className="dash-section-title">Your habits &amp; goals</h2>
          <p className="dash-section-sub">
            Personal to this account – check-ins, savings targets and bank connection.
          </p>
        </div>

        <div className="dashboard-grid dashboard-grid-2">
          {/* Streak */}
          <div className="dashboard-card">
            <div className="habits-header">
              <h3>Check-in streak</h3>
              <span className="habits-pill">Daily check-in</span>
            </div>
            <div className="habits-main">
              <div className="habits-number">
                🔥 {streak} day{streak === 1 ? '' : 's'}
              </div>
              <div className="habits-text">
                <div className="habits-line">
                  You&apos;ve opened Snap2Excel {streak} day
                  {streak === 1 ? '' : 's'} in a row.
                </div>
                <div className="habits-sub">
                  Keep nudging this number up – small check-ins keep you in
                  control.
                </div>
              </div>
            </div>
          </div>

          {/* Bank connect */}
          <BankConnectCard />
        </div>

        {/* Goals full width under streak/bank */}
        <div className="dashboard-card" style={{ marginTop: 16 }}>
          <div className="dash-card-header">
            <h3>Saving goals</h3>
            <button
              className="btn btn-secondary"
              style={{ padding: '4px 10px', fontSize: 12 }}
              onClick={addGoal}
            >
              + Add
            </button>
          </div>
          {goals.length === 0 && (
            <p className="dash-muted">
              Create a goal and we&apos;ll track your progress only for this account.
            </p>
          )}
          {goals.map((g) => {
            const pct = Math.min(100, (g.saved / g.target) * 100);
            return (
              <div key={g.id} style={{ marginBottom: 10 }}>
                <div className="dash-goal-top">
                  <span>{g.name}</span>
                  <span>
                    £{g.saved.toFixed(2)} / £{g.target.toFixed(2)}
                  </span>
                </div>
                <div className="goal-progress">
                  <div
                    className="goal-progress__bar"
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <div className="dash-goal-actions">
                  <button
                    className="btn btn-secondary"
                    style={{ padding: '2px 8px', fontSize: 11 }}
                    onClick={() => updateGoalSaved(g)}
                  >
                    Add £
                  </button>
                  <button
                    className="btn btn-secondary"
                    style={{
                      padding: '2px 8px',
                      fontSize: 11,
                      opacity: 0.7,
                    }}
                    onClick={() => deleteGoal(g)}
                  >
                    Remove
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ===== SPENDING PATTERN ===== */}
      <section className="dash-section">
        <div className="dash-section-header">
          <h2 className="dash-section-title">Spending pattern</h2>
          <p className="dash-section-sub">
            Based only on receipts you&apos;ve scanned in this account.
          </p>
        </div>

        <div className="dashboard-grid dashboard-grid-3">
          {/* Category breakdown */}
          <div className="dashboard-card">
            <h3>Top categories</h3>
            {categoryList.length === 0 && (
              <p className="dash-muted">
                Scan a few receipts to see category breakdown.
              </p>
            )}
            {categoryList.map((c) => (
              <div key={c.name} className="dash-cat-row">
                <div className="dash-cat-dot" />
                <div className="dash-cat-name">{c.name}</div>
                <strong>£{c.total.toFixed(2)}</strong>
              </div>
            ))}
          </div>

          {/* Weekly trend */}
          <div className="dashboard-card">
            <div className="dash-card-header">
              <h3>Last 7 days</h3>
              <span className="trend-total">
                Total: £{weekSpend.toFixed(2)}
              </span>
            </div>
            <div className="trend-bars">
              {trendByDay.map((d) => (
                <div key={d.label} className="trend-bars__item">
                  <div
                    className="trend-bars__bar"
                    style={{ height: `${d.height}%` }}
                    title={`£${d.value.toFixed(2)}`}
                  />
                  <div className="trend-bars__amount">
                    {d.value > 0 ? `£${d.value.toFixed(0)}` : ''}
                  </div>
                  <div className="trend-bars__label">{d.label}</div>
                </div>
              ))}
            </div>
            <p className="dash-muted" style={{ marginTop: 6 }}>
              Highest day: <strong>{trendMax.label}</strong> (
              £{trendMax.value.toFixed(2)}).
            </p>
          </div>

          {/* Insights */}
          <div className="dashboard-card">
            <h3>Spending insights</h3>
            {(!insights || insights.length === 0) && (
              <p className="dash-muted">
                Once you have some receipts, we&apos;ll highlight patterns here.
              </p>
            )}
            <ul className="dash-insights-list">
              {insights.map((text, idx) => (
                <li key={idx}>{text}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* NOTES */}
      <section className="dash-section">
        <div className="dashboard-card">
          <h3>Your notes</h3>
          <textarea
            className="dash-notepad"
            placeholder="Write ideas, bills, shopping reminders, goals… anything."
            value={notes}
            onChange={(e) => {
              const val = e.target.value;
              setNotes(val);
              localStorage.setItem(`${userKeyPrefix}notes`, val);
            }}
          />
        </div>
      </section>

      {/* AI TIP */}
      <section className="dash-section">
        <div className="dashboard-card">
          <h3>AI money tip</h3>
          <p style={{ fontSize: 14 }}>
            {tip || 'Tips appear here once your account has some data.'}
          </p>
        </div>
      </section>
    </>
  );

  /* ========== Final render: embedded vs full page ========== */

  if (embedded) {
    // When used inside /app, just render the cards
    return content;
  }

  // Full-page version for /dashboard route
  return (
    <div className="app-root">
      <div className="app-container">
        <header className="dash-hero">
          <button
            className="btn btn-secondary dash-hero-back"
            onClick={() => navigate('/app')}
          >
            ← Back
          </button>

          <div className="dash-hero-left">
            <h1 className="dash-hero-title">Welcome back 👋</h1>
            <p className="dash-hero-sub">
              Simple view of your habits, goals and recent spending for this account only.
            </p>
          </div>

          <div className="dash-hero-actions">
            <button
              className="dash-hero-btn primary"
              type="button"
              onClick={() => navigate('/app')}
            >
              Scan receipt
            </button>
            <button
              className="dash-hero-btn"
              type="button"
              onClick={() => navigate('/app')}
            >
              Add manually
            </button>
            <button
              className="dash-hero-btn"
              type="button"
              onClick={() => navigate('/app')}
            >
              Export / reports
            </button>
          </div>
        </header>

        {content}
      </div>
    </div>
  );
}
